import { useEffect, useRef, useState } from 'react'

export interface UseCountUpOptions {
  duration?: number
  decimals?: number
  /** Gate the animation — pair with `useScrollReveal`'s `inView` */
  start?: boolean
}

/**
 * Animated count-up value. Animates from 0 to `target` with an ease-out
 * cubic curve once `start` becomes true.
 */
export function useCountUp(target: number, options: UseCountUpOptions = {}): number {
  const { duration = 1400, decimals = 0, start = true } = options
  const [value, setValue] = useState(0)
  const frame = useRef<number | undefined>(undefined)
  const started = useRef(false)

  useEffect(() => {
    if (!start || started.current) return
    started.current = true

    const startTime = performance.now()
    const tick = (now: number) => {
      const t = Math.min(1, (now - startTime) / duration)
      const eased = 1 - Math.pow(1 - t, 3)
      setValue(target * eased)
      if (t < 1) frame.current = requestAnimationFrame(tick)
    }
    frame.current = requestAnimationFrame(tick)
    return () => {
      if (frame.current !== undefined) cancelAnimationFrame(frame.current)
    }
  }, [start, target, duration])

  return decimals > 0 ? Number(value.toFixed(decimals)) : Math.round(value)
}
